import React, { forwardRef, useId, useMemo, useRef, useState } from 'react'

import { cn } from 'utils'
import { mergeRefs } from 'utils/ref_utils'

import { CalendarButton } from './CalendarButton'
import { SolidCircleCheckIcon } from '../../icons/SolidCircleCheck'
import { TextLikeFormField, type TextLikeFormFieldProps } from '../../TextLikeFormField'
import { Popper } from '../Popper'
import { CalendarDialog } from './CalendarDialog'
import { DateSegment } from './DateSegment'
import { ProxyDateInput } from './ProxyDateInput'
import { SegmentSeparator } from './SegmentSeparator'

export interface DateInputProps extends Omit<TextLikeFormFieldProps, 'className' | 'children'>, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className' | 'onClick' | 'onMouseEnter' | 'onMouseLeave' | 'onPointerDown'> {
  disabled?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLButtonElement>;
  onClick?: React.MouseEventHandler<HTMLFieldSetElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLButtonElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLFieldSetElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLFieldSetElement>;
  onPointerDown?: React.PointerEventHandler<HTMLFieldSetElement>;
  portalContainer?: HTMLElement;
  validated?: boolean;
  withPortal?: boolean;
}

function parseValue (value: DateInputProps['defaultValue']) {
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value)

    if (Number.isNaN(date.getTime())) {
      return { year: undefined, month: undefined, day: undefined }
    }

    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1, // Months are zero-indexed in `Date` objects 🤷
      day: date.getUTCDate()
    }
  }

  return { year: undefined, month: undefined, day: undefined }
}

function padDateSegment (value: number, length: number) {
  return String(value).padStart(length, '0')
}

// Constructs a `Date` object from the given year, month, and day values, taking
// into account some quirks about the `Date` constructor.
function getDateFromSegments ({ year, month, day }: { year: number | null; month: number | null; day: number | null }) {
  if (year !== null && month !== null && day !== null) {
    // Month values are zero-indexed in `Date` objects 🤷
    const date = new Date(year, month - 1, day)

    // `Date` objects constructed with a year between 0 and 99 will be
    // interpreted as years between 1900 and 1999, so we set the full
    // year explicitly after constructing it.
    date.setFullYear(year)

    return date
  }

  return null
}

/**
 * Evaluates whether the given year, month, and day values represent an invalid,
 * or non-existent, date.
 *
 * Javascript `Date` objects automatically "roll over" dates when constructed
 * with month or day values that are out-of-bounds, so we can detect invalid
 * values by comparing the constructed date's year, month, and day to the
 * provided values.
 */
function isInvalidDate ({ year, month, day }: { year: number | null; month: number | null; day: number | null }) {
  const date = getDateFromSegments({ year, month, day })

  if (date) {
    return date.getFullYear() !== year || date.getMonth() !== month! - 1 || date.getDate() !== day
  }

  return false
}

/**
 * Returns a date string in the format `YYYY-MM-DD` from the given year, month,
 * and day values.
 *
 * Like native date inputs, if the provided year, month, and day
 * values do not result in a valid date (e.g. a non-existent date like February
 * 30 was provided) it returns an empty string.
 */
function getDateString ({ year, month, day }: { year: number | null; month: number | null; day: number | null }) {
  if (year === null || month === null || day === null) return ''
  if (isInvalidDate({ year, month, day })) return ''

  return `${padDateSegment(year, 4)}-${padDateSegment(month, 2)}-${padDateSegment(day, 2)}`
}

function getInitialSegmentValues (value: DateInputProps['value'], defaultValue: DateInputProps['defaultValue']) {
  const {
    year: parsedDefaultYear,
    month: parsedDefaultMonth,
    day: parsedDefaultDay,
  } = parseValue(defaultValue)

  const {
    year: parsedYear,
    month: parsedMonth,
    day: parsedDay,
  } = parseValue(value)

  return {
    initialYear: parsedDefaultYear ?? parsedYear ?? null,
    initialMonth: parsedDefaultMonth ?? parsedMonth ?? null,
    initialDay: parsedDefaultDay ?? parsedDay ?? null,
  }
}

/**
 * Executes a callback during the render phase when the external `value`
 * changes, to be used to synchronize the local state with the external value.
 * This is similar to the old `getDerivedStateFromProps` lifecycle method on
 * class-based React components, which existed for rare use cases but was
 * generally discouraged. For function components, handling updates in a
 * `useEffect` hook is generally preferred but for this specific component its
 * important to be able to update the state synchronously before the next
 * render cycle.
 */
function useControlledValueChanges (
  callback: () => void,
  value: DateInputProps['value']
) {
  const previousControlledValueRef = useRef(value)

  if (previousControlledValueRef.current !== value) {
    callback()
    previousControlledValueRef.current = value
  }
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(({
  defaultValue,
  disabled,
  max,
  min,
  onBlur,
  onClick,
  onFocus,
  portalContainer = document.body,
  withPortal = true,
  validated,
  value,
  ...props
}, forwardedRef) => {
  const { initialYear, initialMonth, initialDay } = useMemo(
    () => getInitialSegmentValues(value, defaultValue),
    [value, defaultValue]
  )

  const [year, setYear] = useState(initialYear)
  const [month, setMonth] = useState(initialMonth)
  const [day, setDay] = useState(initialDay)

  const [showCalendar, setShowCalendar] = useState(false)

  const monthInputRef = useRef<HTMLInputElement>(null)
  const dayInputRef = useRef<HTMLInputElement>(null)
  const yearInputRef = useRef<HTMLInputElement>(null)
  const dateInputRef = useRef<HTMLInputElement>(null)
  const formFieldRef = useRef<HTMLFieldSetElement | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  const dialogId = useId()

  const date = useMemo(() => {
    return getDateString({ year, month, day }) || null
  }, [year, month, day])

  useControlledValueChanges(() => {
    const filledSegments = [year, month, day].filter(Boolean)
    const { year: parsedYear, month: parsedMonth, day: parsedDay } = parseValue(value)

    let shouldUpdate = false

    if (filledSegments.length === 3 && value !== date) {
      // If all segments are filled and the external value differs from the date
      // derived from local state, we should update the local state to match.
      // This allows the date segments to be updated or cleared externally.
      shouldUpdate = true
    } else if (filledSegments.length > 0 && value) {
      // If only _some_ segments are filled and the external value is not empty,
      // we should update the local state to match. This allows the date
      // segments to be updated from an incomplete state to a completed state.
      // Notably, this does _not_ allow incomplete date segments to be cleared
      // externally, prioritizing the user's ability to continue to edit the
      // segments one-by-one when the external value was cleared by the user
      // deleting or invalidating a date segment.
      shouldUpdate = true
    } else if (filledSegments.length === 0) {
      // If no segments are filled, sync the local state to the external value.
      shouldUpdate = true
    }

    if (shouldUpdate) {
      // We want to update the local state and the proxied input's `value` to
      // match the external value, but we _don't_ want to emit a change event
      // to the owner component because a) the owner component already knows
      // about the change and b) this code executes synchronously during the
      // render phase and it would trigger a React warning to try to update
      // the owner's state during this phase.
      updateDate(
        {
          year: parsedYear ?? null,
          month: parsedMonth ?? null,
          day: parsedDay ?? null,
        },
        false
      )
    }
  }, value)

  function updateDate (
    {
      year,
      month,
      day
    }: { year: number | null; month: number | null; day: number | null },
    emitChange = true
  ) {
    setYear(year)
    setMonth(month)
    setDay(day)

    const nextDate = getDateString({ year, month, day })

    // React overrides the setter for `value` so we need to use
    // `Object.getOwnPropertyDescriptor` to get the original setter
    const setValue = Object?.getOwnPropertyDescriptor(
      HTMLInputElement.prototype,
      'value'
    )?.set

    setValue?.call(dateInputRef.current, nextDate)

    if (emitChange) {
      dateInputRef.current?.dispatchEvent(
        new Event('input', { bubbles: true })
      )
    }
  }

  function getFieldsetDescription () {
    if (date) {
      const dateString = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })

      return `Current date: ${dateString}`
    }

    return 'Empty date'
  }

  function handleBlur (event: React.FocusEvent<HTMLInputElement | HTMLButtonElement>) {
    const formField = formFieldRef.current
    const dialog = dialogRef.current

    // For `blur` events, `event.relatedTarget` is the element that gained focus
    const currentlyFocusedElement = event.relatedTarget

    const currentlyFocusedInField = formField?.contains(currentlyFocusedElement as HTMLElement)
    const currentlyFocusedInDialog = dialog?.contains(currentlyFocusedElement as HTMLElement)

    if (!currentlyFocusedInField && !currentlyFocusedInDialog) {
      onBlur?.(event)
    }
  }

  function handleFocus (event: React.FocusEvent<HTMLInputElement | HTMLButtonElement>) {
    const formField = formFieldRef.current
    const dialog = dialogRef.current

    // For `focus` events, `event.relatedTarget` is the element that lost focus
    const previouslyFocusedElement = event.relatedTarget

    const previouslyFocusedInField = formField?.contains(previouslyFocusedElement as HTMLElement)
    const previouslyFocusedInDialog = dialog?.contains(previouslyFocusedElement as HTMLElement)
    const previouslyFocusedOnProxiedInput = previouslyFocusedElement === dateInputRef.current

    if ((!previouslyFocusedInField && !previouslyFocusedInDialog) || previouslyFocusedOnProxiedInput) {
      onFocus?.(event)
    }
  }

  function handleKeyDown (event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === ' ') {
      event.preventDefault()
      setShowCalendar(true)
    }
  }

  function handlePaste (event: React.ClipboardEvent<HTMLInputElement>) {
    const pastedValue = event.clipboardData.getData('text')
    const { year, month, day } = parseValue(pastedValue)

    if (year === undefined || month === undefined || day === undefined) return

    event.preventDefault()

    updateDate({ year, month, day })

    props.onPaste?.(event)
  }

  return (
    <Popper>
      <TextLikeFormField {...props}>
        {(formFieldProps, ids) => (
          <>
            {
              // Users will interact with the `<DateSegment>` inputs or the
              // calendar dialog, but we're proxying value changes through a
              // native `<input type="date">` element so that consumers of the
              // `<DateInput>` can use it like they would use a native date
              // input, (i.e. attaching `onChange` handlers, reading/setting
              // its value from the DOM, moving focus programmatically, using
              // HTML5 form validation, etc.)
            }
            <ProxyDateInput
              {...formFieldProps}
              aria-invalid={
                formFieldProps['aria-invalid'] ||
                isInvalidDate({ year, month, day })
              }
              date={date}
              disabled={disabled}
              max={max}
              min={min}
              onChange={props.onChange}
              onFocus={(event) => {
                event.preventDefault()
                monthInputRef.current?.focus()
              }}
              onValueChange={(value) => {
                const { year, month, day } = parseValue(value)

                updateDate({
                  year: year ?? null,
                  month: month ?? null,
                  day: day ?? null,
                })
              }}
              ref={mergeRefs(dateInputRef, forwardedRef)}
            />
            <Popper.Anchor asChild>
              <fieldset
                aria-label={getFieldsetDescription()}
                aria-describedby={[
                  ids.labelId,
                  ids.descriptionId,
                  ids.errorMessageId,
                ]
                  .filter(Boolean)
                  .join(' ')}
                className={cn(formFieldProps.className, {
                  'pr-8': !validated,
                  'pr-12': validated,
                })}
                onClick={(event) => {
                  const clickedThisElement =
                    event.target === event.currentTarget
                  const dateSegmentIsFocused = document.activeElement?.closest(
                    '[data-date-segment]'
                  )

                  // If the user clicks this container while no date segment is
                  // focused, focus the first date segment.
                  if (clickedThisElement && !dateSegmentIsFocused) {
                    monthInputRef.current?.focus()
                  }

                  onClick?.(event)
                }}
                onMouseDown={(event) => {
                  const clickedThisElement =
                    event.target === event.currentTarget
                  const dateSegmentIsFocused = document.activeElement?.closest(
                    '[data-date-segment]'
                  )

                  // If the user clicks this container while a date segment is
                  // focused, prevent the default behavior to avoid losing focus.
                  if (clickedThisElement && dateSegmentIsFocused) {
                    event.preventDefault()
                  }
                }}
                onMouseEnter={props.onMouseEnter}
                onMouseLeave={props.onMouseLeave}
                onPointerDown={props.onPointerDown}
                ref={(element) => {
                  // We need to make sure we don't lose a reference to the DOM
                  // node between renders.
                  if (element) {
                    formFieldRef.current = element
                  }
                }}
              >
                <DateSegment
                  aria-describedby={ids.labelId}
                  aria-invalid={
                    formFieldProps['aria-invalid'] ||
                    isInvalidDate({ year, month, day })
                  }
                  disabled={disabled}
                  onBlur={handleBlur}
                  onChange={(value) => {
                    updateDate({
                      year,
                      month: value,
                      day,
                    })
                  }}
                  onFocus={handleFocus}
                  onKeyDown={handleKeyDown}
                  onNext={() => {
                    dayInputRef.current?.focus()
                  }}
                  onPaste={handlePaste}
                  placeholder='mm'
                  ref={monthInputRef}
                  segment='month'
                  value={month ?? null}
                />
                <SegmentSeparator />
                <DateSegment
                  aria-describedby={ids.labelId}
                  aria-invalid={
                    formFieldProps['aria-invalid'] ||
                    isInvalidDate({ year, month, day })
                  }
                  disabled={disabled}
                  onBlur={handleBlur}
                  onChange={(value) => {
                    updateDate({
                      year,
                      month,
                      day: value,
                    })
                  }}
                  onFocus={handleFocus}
                  onKeyDown={handleKeyDown}
                  onNext={() => {
                    yearInputRef.current?.focus()
                  }}
                  onPaste={handlePaste}
                  onPrevious={() => {
                    monthInputRef.current?.focus()
                  }}
                  placeholder='dd'
                  ref={dayInputRef}
                  segment='day'
                  value={day ?? null}
                />
                <SegmentSeparator />
                <DateSegment
                  aria-describedby={ids.labelId}
                  aria-invalid={
                    formFieldProps['aria-invalid'] ||
                    isInvalidDate({ year, month, day })
                  }
                  disabled={disabled}
                  onBlur={handleBlur}
                  onChange={(value) => {
                    updateDate({
                      year: value,
                      month,
                      day,
                    })
                  }}
                  onFocus={handleFocus}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  onPrevious={() => {
                    dayInputRef.current?.focus()
                  }}
                  placeholder='yyyy'
                  ref={yearInputRef}
                  segment='year'
                  value={year ?? null}
                />
                {!disabled && (
                  <div className='flex items-center absolute right-2 top-0 bottom-0'>
                    <CalendarButton
                      aria-haspopup='dialog'
                      aria-expanded={showCalendar}
                      aria-controls={dialogId}
                      onBlur={handleBlur}
                      onClick={() => {
                        if (!showCalendar) setShowCalendar(true)
                      }}
                      onFocus={handleFocus}
                    />
                    {validated && (
                      <span className='pointer-events-none text-green-400'>
                        <SolidCircleCheckIcon size={16} />
                      </span>
                    )}
                  </div>
                )}
              </fieldset>
            </Popper.Anchor>
          </>
        )}
      </TextLikeFormField>
      <CalendarDialog
        date={date}
        dialogRef={dialogRef}
        id={dialogId}
        max={max}
        min={min}
        onDateChange={(date) => {
          const year = date?.getFullYear() ?? null
          const month = date?.getMonth() ?? null
          const day = date?.getDate() ?? null

          setShowCalendar(false)

          updateDate({
            year,
            month: month === null ? null : month + 1,
            day,
          })
        }}
        portalContainer={portalContainer}
        setShowCalendar={setShowCalendar}
        showCalendar={showCalendar}
        withPortal={withPortal}
      />
    </Popper>
  )
})
